import { useState } from 'react';
import { AlertTriangle, Check, ChevronRight, Layers, Maximize2, Minus, PackagePlus, Plus, Ruler, ScanLine, Truck, X } from 'lucide-react';
import Sheet from './Sheet';
import ProductImage from './ProductImage';
import DestinationChips from './DestinationChips';
import { LineTags } from './Tags';
import { KINDS } from '../lib/verdict';
import { boxDims, boxLabel, isMultiBox, looseBoxes } from '../lib/boxes';
import { cn, fmtVolume, fmtWeight, qty } from '../lib/utils';

export default function QtySheet({ line, count = 0, boxCounts = {}, dest, onChange, onBoxChange, onAddBox, onEditBox, onFlags, onClose }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [flagBusy, setFlagBusy] = useState(null);
  const [flagError, setFlagError] = useState('');
  if (!line) return null;
  const expected = line.to_receive;
  const done = line.status === 'done';
  const multi = isMultiBox(line);
  const loose = looseBoxes(line, boxCounts, count);
  const confirmed = line.packaging_status === 'confirmed';
  // Odoo refuses the receipt of a product without confirmed packaging, so the
  // packaging comes first in the sheet until every box is measured and confirmed.
  const packagingFirst = !done && !confirmed;

  const set = (n) => onChange?.(Math.max(0, n));
  const commitDraft = () => {
    // an empty field keeps the count, and a barcode scanned into it is not a quantity
    const n = Number(draft);
    if (draft.trim() !== '' && Number.isFinite(n) && n <= 99999) set(n);
    setEditing(false);
  };

  const toggle = async (key, value) => {
    setFlagBusy(key);
    setFlagError('');
    try {
      await onFlags?.(line, { [key]: value });
    } catch (err) {
      setFlagError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setFlagBusy(null);
    }
  };

  const flagChip = (key, label, Icon, active, tone) => (
    <button
      type="button"
      onClick={() => toggle(key, !active)}
      disabled={flagBusy !== null}
      aria-pressed={active}
      className={cn(
        'inline-flex h-10 items-center gap-1.5 rounded-full border px-3 text-[14px] font-semibold transition-colors disabled:opacity-60',
        active ? cn(tone, 'border-tone bg-tone-bg text-tone') : 'border-line bg-surface text-ink2 active:bg-sunken'
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
      {flagBusy === key && <span className="text-[12px] font-normal">…</span>}
    </button>
  );

  const packagingSection = renderPackaging();

  return (
    <Sheet open onClose={onClose} label="Line details">
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-start gap-3">
          <ProductImage src={line.image} alt="" className="h-20 w-20" />
          <div className="min-w-0 flex-1">
            <p className="text-[17px] font-semibold leading-tight">{line.name}</p>
            <p className="mt-1 text-[14px] text-ink2">
              {line.vendor_code && <span className="font-mono font-semibold text-ink">{line.vendor_code}</span>}
              {line.vendor_code && !multi && line.boxes[0]?.vendor_barcode && ' · '}
              {!multi && line.boxes[0]?.vendor_barcode && <span className="font-mono tabular-nums">EAN {line.boxes[0].vendor_barcode}</span>}
              {(line.vendor_code || (!multi && line.boxes[0]?.vendor_barcode)) && <br />}
              ordered {qty(line.ordered)}, received {qty(line.received)}
            </p>
            {line.shipped_pending && (
              <p className="mt-1 inline-flex items-center gap-1 text-[14px] font-semibold">
                <Truck className="h-4 w-4" /> Supplier shipped {qty(line.shipped)}
              </p>
            )}
            <LineTags line={line} className="mt-1.5" />
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>

        {packagingFirst && packagingSection}

        {!done && (
          <p className="mt-4 text-[14px] text-ink2">
            Expected: <span className="font-semibold text-ink">{qty(expected)} {expected === 1 ? 'unit' : 'units'}</span>
            {multi && (
              <>
                {' '}× {line.boxes.length} boxes = <span className="font-semibold text-ink">{qty(line.cartons)} cartons</span>
              </>
            )}
            {line.line_volume_l && (
              <span className="block">
                About <span className="font-semibold text-ink">{fmtVolume(line.line_volume_l)}</span>
                {line.line_weight_kg ? <> and <span className="font-semibold text-ink">{fmtWeight(line.line_weight_kg)}</span></> : null} for the whole line
                {expected > 1 && line.volume_l ? ` (${fmtVolume(line.volume_l)} per unit)` : ''}
              </span>
            )}
          </p>
        )}

        {!done && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <button type="button" onClick={() => set(count - 1)} disabled={count <= 0} aria-label="One unit less" className="flex h-16 w-16 items-center justify-center rounded-2xl bg-sunken text-ink active:bg-line disabled:opacity-40">
              <Minus className="h-7 w-7" />
            </button>
            {editing ? (
              <div className="flex-1 text-center">
                <input autoFocus type="number" inputMode="numeric" min="0" aria-label="Quantity" value={draft} onFocus={(e) => e.target.select()} onChange={(e) => setDraft(e.target.value)} onBlur={commitDraft} onKeyDown={(e) => e.key === 'Enter' && commitDraft()} className="font-cond w-32 rounded-xl border-2 border-line bg-surface text-center text-[44px] font-bold leading-none focus:border-ink" />
                {multi && <span className="block text-[13px] text-ink2">complete units</span>}
              </div>
            ) : (
              <button type="button" onClick={() => { setDraft(String(count)); setEditing(true); }} className="flex-1 text-center" aria-label="Type a quantity">
                <span className={cn('font-cond text-[56px] font-bold leading-none', count > expected && 'text-alert', count === expected && expected > 0 && 'text-out')}>
                  {qty(count)}
                  <span className="text-[26px] font-semibold text-ink2"> / {qty(expected)}</span>
                </span>
                {multi && <span className="block text-[13px] text-ink2">complete units</span>}
              </button>
            )}
            <button type="button" onClick={() => set(count + 1)} aria-label="One unit more" className="flex h-16 w-16 items-center justify-center rounded-2xl bg-ink text-bg active:opacity-80">
              <Plus className="h-7 w-7" />
            </button>
          </div>
        )}
        {!done && (
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => set(expected)} className="flex-1 rounded-xl bg-sunken py-3 text-[14px] font-semibold active:bg-line">
              All {qty(expected)} received
            </button>
            <button type="button" onClick={() => set(0)} className="rounded-xl bg-sunken px-4 py-3 text-[14px] font-semibold active:bg-line">
              Reset
            </button>
          </div>
        )}

        {!done && multi && (
          <section className="mt-5">
            <h3 className="text-[13px] font-semibold text-ink2">Boxes per unit</h3>
            <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
              {line.boxes.map((b, i) => {
                const c = Number(boxCounts[b.id] || 0);
                const full = c >= expected;
                return (
                  <li key={b.id} className="flex items-center gap-2 px-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-semibold">{boxLabel(b, i)}</span>
                      <span className="block font-mono text-[12px] tabular-nums text-ink2">{b.vendor_barcode || 'no vendor EAN'}</span>
                    </span>
                    <button type="button" onClick={() => onBoxChange?.(b.id, Math.max(0, c - 1))} disabled={c <= 0} aria-label={`One ${boxLabel(b, i)} less`} className="flex h-10 w-10 items-center justify-center rounded-xl bg-sunken active:bg-line disabled:opacity-40">
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className={cn('font-cond w-14 text-center text-[24px] font-bold leading-none', full && c === expected && 'text-out', c > expected && 'text-alert', c === 0 && 'text-ink2')}>
                      {qty(c)}<span className="text-[15px] font-semibold text-ink2">/{qty(expected)}</span>
                    </span>
                    <button type="button" onClick={() => onBoxChange?.(b.id, c + 1)} aria-label={`One ${boxLabel(b, i)} more`} className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-bg active:opacity-80">
                      <Plus className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
            {loose > 0 && (
              <p className="mt-2 text-[13px] text-hold">
                {qty(loose)} {loose === 1 ? 'carton is' : 'cartons are'} waiting for the rest of {loose === 1 ? 'its' : 'their'} set. Only complete units are confirmed.
              </p>
            )}
          </section>
        )}

        {!done && (
          <section className="mt-5">
            <h3 className="text-[13px] font-semibold text-ink2">Where it goes</h3>
            <DestinationChips dest={dest} className="mt-1.5" />
            {dest?.orders?.length > 0 && (
              <ul className="mt-2 divide-y divide-line rounded-xl border border-line">
                {dest.orders.map((o, i) => (
                  <li key={`${o.ref}-${i}`} className={cn(KINDS[o.kind].tone, 'flex items-start gap-3 px-3 py-2')}>
                    <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-tone" />
                    <span className="min-w-0 flex-1 text-[14px]">
                      <span className="font-semibold">{o.ref}</span>
                      {o.customer && <span className="text-ink2"> · {o.customer}</span>}
                      <span className="text-ink2"> · {qty(o.qty)} pc</span>
                      {o.kind === 'hold' && <span className="block text-[13px] text-ink2">set aside: needs {o.waiting_on.join(', ')} from this delivery, not scanned yet</span>}
                      {o.kind === 'crossdock' && o.waiting_on?.length > 0 && <span className="block text-[13px] text-ink2">waits on {o.waiting_on.join(', ')}</span>}
                      {o.kind === 'out' && <span className="block text-[13px] text-ink2">last missing item, ship it</span>}
                    </span>
                    <span className="text-[12px] font-bold text-tone">{KINDS[o.kind].word}</span>
                  </li>
                ))}
              </ul>
            )}
            {dest && !dest.orders?.length && <p className="mt-1 text-[14px] text-ink2">Bought for stock. No customer waiting.</p>}
          </section>
        )}

        {!packagingFirst && packagingSection}

        <section className="mt-5">
          <h3 className="text-[13px] font-semibold text-ink2">Handling <span className="font-normal">· a tap saves it on the product in Odoo</span></h3>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {flagChip('fragile', 'Fragile', AlertTriangle, line.fragile, 'tone-over')}
            {flagChip('shipped_on_pallet', 'Ships on pallet', Layers, line.pallet, 'tone-stock')}
            {flagChip('oversize', 'Oversize', Maximize2, line.oversize, 'tone-crossdock')}
            {flagChip('no_ean_from_vendor', 'No EAN from supplier', ScanLine, line.no_vendor_ean, 'tone-crossdock')}
          </div>
          {flagError && <p className="mt-2 rounded-xl bg-alert-bg px-3 py-2 text-[13px] text-alert">{flagError}</p>}
        </section>
      </div>
    </Sheet>
  );

  /* Packaging: what is on file, fix it, confirm it. Rendered first while the
     data is incomplete, after the count once it is confirmed. */
  function renderPackaging() {
    return (
        <section className={cn('mt-4', packagingFirst && 'rounded-xl bg-alert-bg px-3 pb-3 pt-2')}>
          {packagingFirst && (
            <p className="mb-2 flex items-start gap-2 text-[13px] text-alert">
              <Ruler className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <span className="font-semibold">Complete the packaging first.</span> Odoo will not accept this product on the receipt until every box has a size and a weight and the packaging is confirmed.
              </span>
            </p>
          )}
          <div className="flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-ink2">Packaging</h3>
            {line.packaging_status === 'confirmed' && (
              <span className="tone-out inline-flex items-center gap-1 rounded-md bg-tone-bg px-1.5 py-0.5 text-[12px] font-semibold text-tone">
                <Check className="h-3.5 w-3.5" /> Confirmed
              </span>
            )}
            {line.packaging_status === 'unconfirmed' && (
              <span className="tone-crossdock inline-flex items-center rounded-md bg-tone-bg px-1.5 py-0.5 text-[12px] font-semibold text-tone">Not confirmed yet</span>
            )}
          </div>
          {line.boxes.length === 0 ? (
            <p className="mt-1 text-[14px] text-ink2">No box on file. Measure the carton and add it; volume and weight only count once the packaging is confirmed.</p>
          ) : (
            <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
              {line.boxes.map((b, i) => {
                const complete = b.length && b.width && b.height && b.weight;
                return (
                  <li key={b.id}>
                    <button type="button" onClick={() => onEditBox?.(line, b)} className="flex w-full items-center gap-3 px-3 py-2 text-left active:bg-sunken">
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-semibold">{boxLabel(b, i)}</span>
                        <span className={cn('block text-[13px]', complete ? 'text-ink2' : 'text-hold')}>{boxDims(b) || 'no size'}{!complete && boxDims(b) ? ' · incomplete' : ''}</span>
                        <span className="block font-mono text-[12px] tabular-nums text-ink2">{b.vendor_barcode || 'no vendor EAN yet'}</span>
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-[13px] font-semibold text-ink2">Edit <ChevronRight className="h-4 w-4" /></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => onAddBox?.(line)} className={cn('inline-flex h-11 items-center gap-2 rounded-xl px-3 text-[14px] font-semibold', line.boxes.length === 0 ? 'bg-ink text-bg active:opacity-80' : 'bg-sunken active:bg-line')}>
              <PackagePlus className="h-4 w-4" /> {line.boxes.length === 0 ? 'Add box data' : 'Add a box'}
            </button>
            {line.boxes.length > 0 && !confirmed && (
              <button
                type="button"
                onClick={() => toggle('box_confirmed', true)}
                disabled={!line.packaging_complete || flagBusy !== null}
                title={line.packaging_complete ? '' : 'Every box needs size and weight first'}
                className="inline-flex h-11 items-center gap-2 rounded-xl bg-ink px-3 text-[14px] font-semibold text-bg active:opacity-80 disabled:opacity-40"
              >
                <Check className="h-4 w-4" /> Confirm packaging
              </button>
            )}
            {confirmed && (
              <button type="button" onClick={() => toggle('box_confirmed', false)} disabled={flagBusy !== null} className="inline-flex h-11 items-center rounded-xl px-3 text-[13px] font-semibold text-ink2 active:bg-sunken">
                Mark as not confirmed
              </button>
            )}
          </div>
          {line.boxes.length > 0 && !confirmed && !line.packaging_complete && (
            <p className="mt-1.5 text-[13px] text-hold">Every box needs a size and a weight before you can confirm.</p>
          )}
        </section>
    );
  }
}
